import React, { useState, useEffect } from "react";

const API = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
  ? "http://localhost:5000"
  : (process.env.REACT_APP_API_URL || "http://localhost:5000");

const POPULAR_SPORTS = [
  { key: "cricket_ipl", title: "IPL Cricket 🏏", group: "Cricket" },
  { key: "upcoming", title: "Upcoming Games 📅", group: "General" },
  { key: "americanfootball_nfl", title: "NFL US Football 🏈", group: "American Football" },
  { key: "basketball_nba", title: "NBA US Basketball 🏀", group: "Basketball" },
  { key: "soccer_epl", title: "EPL English Premier League ⚽", group: "Soccer" },
  { key: "tennis_atp_french_open", title: "ATP French Open 🎾", group: "Tennis" }
];

const REGIONS = [
  { key: "eu", title: "Europe 🇪🇺" },
  { key: "us", title: "United States 🇺🇸" },
  { key: "uk", title: "United Kingdom 🇬🇧" },
  { key: "au", title: "Australia 🇦🇺" }
];

const MARKETS = [
  { key: "h2h", title: "Moneyline (h2h) 🎯" },
  { key: "spreads", title: "Points Spread 📊" },
  { key: "totals", title: "Over/Under Totals 📈" },
  { key: "outrights", title: "Futures (outrights) 🏆" }
];

export default function OddsApiHub() {
  // Config state
  const [apiKey, setApiKey] = useState("51953d3573f0f9196fbaaf97e8156b41");
  const [saveStatus, setSaveStatus] = useState("");
  const [saveStatusType, setSaveStatusType] = useState("");
  const [loadingConfig, setLoadingConfig] = useState(false);

  // Testing tool state
  const [endpoint, setEndpoint] = useState("/v4/sports");
  const [selectedSport, setSelectedSport] = useState("cricket_ipl");
  const [selectedRegions, setSelectedRegions] = useState(["eu"]);
  const [selectedMarkets, setSelectedMarkets] = useState(["h2h"]);
  const [oddsFormat, setOddsFormat] = useState("decimal");
  const [daysFrom, setDaysFrom] = useState("");
  const [allSports, setAllSports] = useState(false);
  const [eventId, setEventId] = useState("");

  // Query response state
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryResult, setQueryResult] = useState(null);
  const [queryError, setQueryError] = useState("");
  const [copied, setCopied] = useState(false);

  // Load configured API key on mount
  useEffect(() => {
    async function loadConfig() {
      setLoadingConfig(true);
      try {
        const res = await fetch(`${API}/admin/odds-api-key`);
        const data = await res.json();
        if (res.ok && data.key) {
          setApiKey(data.key);
        }
      } catch (err) {
        console.error("Failed to load Odds API configuration:", err);
      }
      setLoadingConfig(false);
    }
    loadConfig();
  }, []);

  // Save config
  const handleSaveConfig = async () => {
    if (!apiKey.trim()) {
      setSaveStatus("API key cannot be empty!");
      setSaveStatusType("error");
      return;
    }
    setLoadingConfig(true);
    setSaveStatus("");
    try {
      const res = await fetch(`${API}/admin/odds-api-key`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key: apiKey.trim() })
      });
      const data = await res.json();
      if (res.ok) {
        setSaveStatus("✅ API Key updated and cached globally!");
        setSaveStatusType("success");
      } else {
        setSaveStatus(data.message || "Failed to update API key.");
        setSaveStatusType("error");
      }
    } catch {
      setSaveStatus("❌ Connection failure: backend server is offline.");
      setSaveStatusType("error");
    }
    setLoadingConfig(false);
    setTimeout(() => setSaveStatus(""), 4000);
  };

  // Toggle Region selections
  const toggleRegion = (reg) => {
    if (selectedRegions.includes(reg)) {
      if (selectedRegions.length > 1) {
        setSelectedRegions(selectedRegions.filter(r => r !== reg));
      }
    } else {
      setSelectedRegions([...selectedRegions, reg]);
    }
  };

  // Toggle Market selections
  const toggleMarket = (mkt) => {
    if (selectedMarkets.includes(mkt)) {
      if (selectedMarkets.length > 1) {
        setSelectedMarkets(selectedMarkets.filter(m => m !== mkt));
      }
    } else {
      setSelectedMarkets([...selectedMarkets, mkt]);
    }
  };

  // Calculate planned quota credit cost dynamically
  const calculateQuotaCost = () => {
    if (endpoint === "/v4/sports" || endpoint === "/v4/sports/{sport}/events") {
      return { cost: 0, text: "🆓 FREE endpoint (does not count against usage credits)" };
    }
    if (endpoint === "/v4/sports/{sport}/odds") {
      const cost = selectedMarkets.length * selectedRegions.length;
      return { cost, text: `⚠️ Costs ${cost} credits (${selectedMarkets.length} market${selectedMarkets.length > 1 ? "s" : ""} × ${selectedRegions.length} region${selectedRegions.length > 1 ? "s" : ""})` };
    }
    if (endpoint === "/v4/sports/{sport}/scores") {
      const cost = daysFrom ? 2 : 1;
      return { cost, text: `⚠️ Costs ${cost} credit${cost > 1 ? "s" : ""} (${daysFrom ? "includes completed matches history" : "live/upcoming matches only"})` };
    }
    if (endpoint === "/v4/sports/{sport}/events/{eventId}/odds") {
      const cost = selectedMarkets.length * selectedRegions.length;
      return { cost, text: `⚠️ Costs ${cost} credits (Single game outcomes: ${selectedMarkets.length} market${selectedMarkets.length > 1 ? "s" : ""} × ${selectedRegions.length} region${selectedRegions.length > 1 ? "s" : ""})` };
    }
    return { cost: 1, text: "Calculated credit quota cost applies." };
  };

  const quotaInfo = calculateQuotaCost();

  // Test the selected endpoint
  const handleTestEndpoint = async () => {
    setQueryLoading(true);
    setQueryResult(null);
    setQueryError("");
    setCopied(false);

    try {
      // Resolve path
      let resolvedPath = endpoint;
      if (resolvedPath.includes("{sport}")) {
        resolvedPath = resolvedPath.replace("{sport}", selectedSport);
      }
      if (resolvedPath.includes("{eventId}")) {
        if (!eventId.trim()) {
          setQueryError("Please provide a valid Event ID!");
          setQueryLoading(false);
          return;
        }
        resolvedPath = resolvedPath.replace("{eventId}", eventId.trim());
      }

      // Build query params
      const params = {};
      params.endpoint = resolvedPath;
      params.apiKey = apiKey.trim();

      if (endpoint === "/v4/sports") {
        if (allSports) params.all = "true";
      } else if (endpoint === "/v4/sports/{sport}/odds" || endpoint === "/v4/sports/{sport}/events/{eventId}/odds") {
        params.regions = selectedRegions.join(",");
        params.markets = selectedMarkets.join(",");
        params.oddsFormat = oddsFormat;
      } else if (endpoint === "/v4/sports/{sport}/scores") {
        if (daysFrom) params.daysFrom = daysFrom;
      }

      const queryString = new URLSearchParams(params).toString();
      const res = await fetch(`${API}/admin/proxy-odds-api?${queryString}`);
      const data = await res.json();

      if (res.ok) {
        setQueryResult(data);
      } else {
        setQueryError(data.message || "Failed to fetch response.");
      }
    } catch (err) {
      setQueryError("Failed to fetch. Check connection to backend proxy.");
    }
    setQueryLoading(false);
  };

  // Copy JSON response to clipboard
  const handleCopyClipboard = () => {
    if (!queryResult) return;
    navigator.clipboard.writeText(JSON.stringify(queryResult.data, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "1.5rem 0" }}>
      {/* Page Header */}
      <div style={{ display: "flex", alignItems: "center", justifyItems: "center", gap: 14, marginBottom: 20 }}>
        <div style={{
          width: 52, height: 52, borderRadius: 14,
          background: "rgba(240, 180, 41, 0.12)", border: "1px solid var(--accent)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 24, boxShadow: "0 4px 20px rgba(240,180,41,0.15)"
        }}>⚙️</div>
        <div>
          <h1 className="screen-title" style={{ fontSize: 24, fontWeight: 800, margin: 0, color: "var(--text)" }}>Odds API Testing & Setup</h1>
          <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 2 }}>Manage dynamic sportsbook endpoints, credentials, and track quota limits in real-time.</p>
        </div>
      </div>

      {/* Grid: Config Setup + Endpoint Configurator */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 20, marginBottom: 24 }}>
        
        {/* Left Card: API Key Credentials */}
        <div style={{
          background: "linear-gradient(135deg, var(--surface) 0%, rgba(20,25,38,0.7) 100%)",
          border: "1px solid var(--border)",
          borderRadius: 16,
          padding: "20px 22px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)"
        }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: "var(--accent)", marginBottom: 4, letterSpacing: "0.02em" }}>🔑 Odds-API Credentials</h2>
          <p style={{ fontSize: 12, color: "var(--muted)", marginBottom: 16 }}>This API key authorizes data synchronizations for real IPL & football matches.</p>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <input
              type="password"
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder="Enter your API Key..."
              style={{
                flex: 1, minWidth: 260,
                background: "rgba(0,0,0,0.2)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: "10px 14px",
                color: "#fff",
                fontFamily: "monospace",
                fontSize: 13,
                outline: "none",
                transition: "border-color 0.2s"
              }}
              onFocus={e => e.target.type = "text"}
              onBlur={e => e.target.type = "password"}
            />
            <button
              onClick={handleSaveConfig}
              disabled={loadingConfig}
              style={{
                padding: "10px 24px",
                borderRadius: 8,
                border: "none",
                background: "linear-gradient(135deg, var(--accent) 0%, var(--accent2) 100%)",
                color: "#000",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                boxShadow: "0 4px 16px var(--btn-shadow)",
                transition: "transform 0.1s"
              }}
              onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
              onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}
            >
              {loadingConfig ? "Syncing..." : "Save & Sync Globally 🔒"}
            </button>
          </div>

          {saveStatus && (
            <div style={{
              marginTop: 12, fontSize: 12, fontWeight: 600,
              color: saveStatusType === "success" ? "var(--green)" : "var(--red)"
            }}>
              {saveStatus}
            </div>
          )}
        </div>

        {/* Right Card: Endpoint Query Builder */}
        <div style={{
          background: "linear-gradient(135deg, var(--surface) 0%, rgba(20,25,38,0.7) 100%)",
          border: "1px solid var(--border)",
          borderRadius: 16,
          padding: "22px 24px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)"
        }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: "var(--accent)", marginBottom: 14 }}>🚀 Live API Endpoint Tester</h2>
          
          {/* Endpoint selection */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, color: "var(--muted)", fontWeight: 700, display: "block", marginBottom: 6, textTransform: "uppercase" }}>Endpoint Path</label>
            <select
              value={endpoint}
              onChange={e => { setEndpoint(e.target.value); setQueryResult(null); }}
              style={{
                width: "100%",
                background: "rgba(0,0,0,0.2)",
                color: "var(--text)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: "10px 12px",
                fontSize: 13,
                outline: "none"
              }}
            >
              <option value="/v4/sports">GET /v4/sports (In-season active sports list) [Quota: 🆓]</option>
              <option value="/v4/sports/{sport}/odds">GET /v4/sports/{"{sport}"}/odds (Bookmaker odds) [Quota: ⚠️]</option>
              <option value="/v4/sports/{sport}/scores">GET /v4/sports/{"{sport}"}/scores (Live and completed scores) [Quota: ⚠️]</option>
              <option value="/v4/sports/{sport}/events">GET /v4/sports/{"{sport}"}/events (Schedule details) [Quota: 🆓]</option>
              <option value="/v4/sports/{sport}/events/{eventId}/odds">GET /v4/sports/{"{sport}"}/events/{"{eventId}"}/odds (Specific match player/prop odds) [Quota: ⚠️]</option>
            </select>
          </div>

          {/* Context Options Grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16, marginBottom: 18 }}>
            
            {/* Sport Selection (Shows for sports endpoints) */}
            {endpoint !== "/v4/sports" && (
              <div>
                <label style={{ fontSize: 11, color: "var(--muted)", fontWeight: 700, display: "block", marginBottom: 6, textTransform: "uppercase" }}>Target Sport</label>
                <select
                  value={selectedSport}
                  onChange={e => setSelectedSport(e.target.value)}
                  style={{
                    width: "100%",
                    background: "rgba(0,0,0,0.2)",
                    color: "var(--text)",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    padding: "9px 12px",
                    fontSize: 13,
                    outline: "none"
                  }}
                >
                  {POPULAR_SPORTS.map(s => (
                    <option key={s.key} value={s.key}>{s.title}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Single Event ID input */}
            {endpoint === "/v4/sports/{sport}/events/{eventId}/odds" && (
              <div>
                <label style={{ fontSize: 11, color: "var(--muted)", fontWeight: 700, display: "block", marginBottom: 6, textTransform: "uppercase" }}>Event ID</label>
                <input
                  type="text"
                  value={eventId}
                  onChange={e => setEventId(e.target.value)}
                  placeholder="e.g. a512a48a58c43290..."
                  style={{
                    width: "100%",
                    background: "rgba(0,0,0,0.2)",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    padding: "9px 12px",
                    color: "#fff",
                    fontSize: 13,
                    outline: "none"
                  }}
                />
              </div>
            )}

            {/* Odds format dropdown */}
            {(endpoint === "/v4/sports/{sport}/odds" || endpoint === "/v4/sports/{sport}/events/{eventId}/odds") && (
              <div>
                <label style={{ fontSize: 11, color: "var(--muted)", fontWeight: 700, display: "block", marginBottom: 6, textTransform: "uppercase" }}>Odds Format</label>
                <select
                  value={oddsFormat}
                  onChange={e => setOddsFormat(e.target.value)}
                  style={{
                    width: "100%",
                    background: "rgba(0,0,0,0.2)",
                    color: "var(--text)",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    padding: "9px 12px",
                    fontSize: 13,
                    outline: "none"
                  }}
                >
                  <option value="decimal">Decimal Odds (e.g. 1.85)</option>
                  <option value="american">American Odds (e.g. -118)</option>
                </select>
              </div>
            )}

            {/* Days From Dropdown for scores */}
            {endpoint === "/v4/sports/{sport}/scores" && (
              <div>
                <label style={{ fontSize: 11, color: "var(--muted)", fontWeight: 700, display: "block", marginBottom: 6, textTransform: "uppercase" }}>Retrieve Past Scores (daysFrom)</label>
                <select
                  value={daysFrom}
                  onChange={e => setDaysFrom(e.target.value)}
                  style={{
                    width: "100%",
                    background: "rgba(0,0,0,0.2)",
                    color: "var(--text)",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    padding: "9px 12px",
                    fontSize: 13,
                    outline: "none"
                  }}
                >
                  <option value="">No history (Live & Upcoming only - 1 credit)</option>
                  <option value="1">Completed within 1 day (2 credits)</option>
                  <option value="2">Completed within 2 days (2 credits)</option>
                  <option value="3">Completed within 3 days (2 credits)</option>
                </select>
              </div>
            )}

            {/* All Sports Checkbox */}
            {endpoint === "/v4/sports" && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 24 }}>
                <input
                  type="checkbox"
                  id="allSports"
                  checked={allSports}
                  onChange={e => setAllSports(e.target.checked)}
                  style={{ width: 16, height: 16, cursor: "pointer" }}
                />
                <label htmlFor="allSports" style={{ fontSize: 13, color: "var(--text)", cursor: "pointer" }}>
                  Include out-of-season sports (`all=true`)
                </label>
              </div>
            )}
          </div>

          {/* Region Chips Selector */}
          {(endpoint === "/v4/sports/{sport}/odds" || endpoint === "/v4/sports/{sport}/events/{eventId}/odds") && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, color: "var(--muted)", fontWeight: 700, display: "block", marginBottom: 8, textTransform: "uppercase" }}>Bookmaker Regions</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {REGIONS.map(reg => {
                  const isSelected = selectedRegions.includes(reg.key);
                  return (
                    <button
                      key={reg.key}
                      onClick={() => toggleRegion(reg.key)}
                      style={{
                        padding: "6px 14px",
                        borderRadius: 20,
                        border: `1px solid ${isSelected ? "var(--accent)" : "var(--border)"}`,
                        background: isSelected ? "rgba(240, 180, 41, 0.08)" : "rgba(0,0,0,0.1)",
                        color: isSelected ? "var(--accent)" : "var(--muted)",
                        fontWeight: isSelected ? 700 : 400,
                        fontSize: 12,
                        cursor: "pointer",
                        transition: "all 0.15s"
                      }}
                    >
                      {reg.title}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Market Chips Selector */}
          {(endpoint === "/v4/sports/{sport}/odds" || endpoint === "/v4/sports/{sport}/events/{eventId}/odds") && (
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: 11, color: "var(--muted)", fontWeight: 700, display: "block", marginBottom: 8, textTransform: "uppercase" }}>Target Markets</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {MARKETS.map(m => {
                  const isSelected = selectedMarkets.includes(m.key);
                  return (
                    <button
                      key={m.key}
                      onClick={() => toggleMarket(m.key)}
                      style={{
                        padding: "6px 14px",
                        borderRadius: 20,
                        border: `1px solid ${isSelected ? "var(--accent)" : "var(--border)"}`,
                        background: isSelected ? "rgba(240, 180, 41, 0.08)" : "rgba(0,0,0,0.1)",
                        color: isSelected ? "var(--accent)" : "var(--muted)",
                        fontWeight: isSelected ? 700 : 400,
                        fontSize: 12,
                        cursor: "pointer",
                        transition: "all 0.15s"
                      }}
                    >
                      {m.title}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quota Cost Indicator Banner */}
          <div style={{
            background: quotaInfo.cost === 0 ? "rgba(34,197,94,0.06)" : "rgba(240,180,41,0.05)",
            border: `1px solid ${quotaInfo.cost === 0 ? "rgba(34,197,94,0.2)" : "rgba(240,180,41,0.25)"}`,
            borderRadius: 10,
            padding: "10px 14px",
            marginBottom: 20,
            fontSize: 12,
            fontWeight: 600,
            color: quotaInfo.cost === 0 ? "var(--green)" : "var(--accent)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}>
            <span>Planned API Usage Cost</span>
            <span>{quotaInfo.text}</span>
          </div>

          {/* Test Submit Button */}
          <button
            onClick={handleTestEndpoint}
            disabled={queryLoading}
            style={{
              width: "100%",
              padding: "12px 0",
              borderRadius: 10,
              border: "none",
              background: "linear-gradient(135deg, var(--accent) 0%, var(--accent2) 100%)",
              color: "#000",
              fontWeight: 800,
              fontSize: 14,
              cursor: "pointer",
              boxShadow: "0 4px 20px var(--btn-shadow)",
              transition: "transform 0.1s"
            }}
            onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
            onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}
          >
            {queryLoading ? "📡 Dispatching Proxy Request..." : "⚡ Send Safe Query →"}
          </button>
        </div>

      </div>

      {/* Response Section */}
      {queryError && (
        <div style={{
          background: "rgba(239, 68, 68, 0.08)",
          border: "1px solid rgba(239,68,68,0.2)",
          borderRadius: 12,
          padding: "14px 18px",
          color: "var(--red)",
          fontSize: 13,
          marginBottom: 24,
          fontWeight: 600
        }}>
          ⚠️ Error dispatching request: {queryError}
        </div>
      )}

      {queryResult && (
        <div style={{
          background: "linear-gradient(135deg, var(--surface) 0%, rgba(20,25,38,0.7) 100%)",
          border: "1px solid var(--border)",
          borderRadius: 16,
          padding: "22px 24px",
          boxShadow: "0 10px 40px rgba(0,0,0,0.35)",
          marginBottom: 30
        }}>
          {/* Top: URL + Status Code */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ fontSize: 10, color: "var(--muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Proxy Target Request</div>
              <div style={{
                background: "rgba(0,0,0,0.3)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: "8px 12px",
                fontSize: 12,
                fontFamily: "monospace",
                color: "#60a5fa",
                wordBreak: "break-all"
              }}>
                GET {queryResult.url}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 10, color: "var(--muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Server Status</div>
              <div style={{
                background: queryResult.status === 200 ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)",
                border: `1px solid ${queryResult.status === 200 ? "var(--green)" : "var(--red)"}`,
                borderRadius: 8,
                padding: "6px 14px",
                fontSize: 13,
                fontWeight: 800,
                color: queryResult.status === 200 ? "var(--green)" : "var(--red)"
              }}>
                {queryResult.status} {queryResult.statusText || "OK"}
              </div>
            </div>
          </div>

          {/* Middle: Quota Dashboard Metrics */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 24 }}>
            {[
              {
                label: "Requests Remaining",
                value: queryResult.headers["x-requests-remaining"] !== null ? queryResult.headers["x-requests-remaining"] : "—",
                color: "var(--green)",
                subtitle: "credits left in quota"
              },
              {
                label: "Requests Used",
                value: queryResult.headers["x-requests-used"] !== null ? queryResult.headers["x-requests-used"] : "—",
                color: "var(--accent)",
                subtitle: "credits spent since reset"
              },
              {
                label: "Last Call Cost",
                value: queryResult.headers["x-requests-last"] !== null ? queryResult.headers["x-requests-last"] : "—",
                color: "#3b82f6",
                subtitle: "credits used by query"
              }
            ].map((stat, i) => (
              <div key={i} style={{
                background: "rgba(0,0,0,0.15)",
                border: "1px solid var(--border)",
                borderRadius: 12,
                padding: "12px 16px",
                position: "relative",
                overflow: "hidden"
              }}>
                <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.02em" }}>{stat.label}</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: stat.color, margin: "8px 0 2px" }}>{stat.value}</div>
                <div style={{ fontSize: 10, color: "var(--muted)" }}>{stat.subtitle}</div>
              </div>
            ))}
          </div>

          {/* Bottom: JSON Inspector */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <span style={{ fontSize: 12, color: "var(--text)", fontWeight: 700 }}>📦 Raw JSON Response Payload</span>
              <button
                onClick={handleCopyClipboard}
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid var(--border)",
                  borderRadius: 6,
                  padding: "4px 12px",
                  fontSize: 11,
                  color: "var(--muted)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  transition: "background 0.2s"
                }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.12)"}
                onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.06)"}
              >
                <span>{copied ? "✅ Copied!" : "📋 Copy Payload"}</span>
              </button>
            </div>
            <div style={{
              background: "#05070c",
              border: "1px solid var(--border)",
              borderRadius: 12,
              padding: "16px",
              maxHeight: "350px",
              overflowY: "auto",
              position: "relative"
            }}>
              <pre style={{
                margin: 0,
                fontSize: 12.5,
                lineHeight: 1.5,
                color: "#e2e8f0",
                fontFamily: "source-code-pro, Menlo, Monaco, Consolas, monospace",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all"
              }}>
                {JSON.stringify(queryResult.data, null, 2)}
              </pre>
            </div>
          </div>

        </div>
      )}
    </div>
  );
}
